import React, { useState } from 'react';
import { toast } from 'sonner';
import apiClient from '../../utils/api';
import './AdminUserManagement.css';

function AdminUserManagement({ users, onUserCreated, onLoginAsUser, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCoinsModal, setShowCoinsModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [assigningUser, setAssigningUser] = useState(null);
  const [coinsUser, setCoinsUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [assignPlan, setAssignPlan] = useState('monthly');
  const [coinsAmount, setCoinsAmount] = useState('');
  const [coinsReason, setCoinsReason] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    setError('');
    setLoading(true);

    try {
      if (editingUser) {
        // Update
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        await apiClient.updateUserByAdmin(editingUser.id, updateData);
        onRefresh();
      } else {
        // Create
        const created = await apiClient.createUserByAdmin(formData);
        onUserCreated(created);
      }
      
      setShowModal(false);
      setEditingUser(null);
      setFormData({ name: '', email: '', password: '', role: 'user' });
    } catch (err) {
      setError(err.message || 'Fehler beim Speichern des Users');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email,
      password: '',
      role: user.role,
    });
    setShowModal(true);
  };

  const handleToggleLock = async (userId, currentLockStatus) => {
    try {
      await apiClient.updateUserByAdmin(userId, { is_locked: !currentLockStatus });
      onRefresh();
      toast.success(currentLockStatus ? '✅ User entsperrt' : '🔒 User gesperrt');
    } catch (error) {
      console.error('Error toggling lock:', error);
      toast.error('Fehler beim Sperren/Entsperren', {
        description: error.message
      });
    }
  };

  const handleTogglePractitioner = async (userId, isPractitioner) => {
    try {
      if (isPractitioner) {
        await apiClient.removePractitionerRole(userId);
      } else {
        await apiClient.assignPractitionerRole(userId);
      }
      onRefresh();
      toast.success(isPractitioner ? '🩺 Practitioner-Rolle entfernt' : '🩺 Practitioner-Rolle zugewiesen');
    } catch (error) {
      console.error('Error toggling practitioner:', error);
      toast.error('Fehler beim Ändern der Practitioner-Rolle', {
        description: error.message
      });
    }
  };

  const handleDelete = async (userId) => {
    try {
      await apiClient.deleteUserByAdmin(userId);
      onRefresh();
      setDeleteConfirm(null);
      toast.success('✅ User erfolgreich gelöscht');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Fehler beim Löschen', {
        description: error.message || 'Unbekannter Fehler'
      });
    }
  };

  const handleAssignSubscription = async () => {
    try {
      // Handle trial and expired separately
      if (assignPlan === 'trial' || assignPlan === 'expired') {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${assigningUser.id}/subscription-status`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: assignPlan
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Fehler beim Ändern des Status');
        }

        const result = await response.json();
        console.log('✅ Status changed:', result);
        
        setShowAssignModal(false);
        setAssigningUser(null);
        onRefresh();
        
        toast.success(
          assignPlan === 'trial' ? '✅ User auf Trial zurückgesetzt' : '✅ Subscription als Expired markiert'
        );
      } else {
        // Regular subscription assignment
        await apiClient.assignSubscription(assigningUser.id, assignPlan);
        setShowAssignModal(false);
        setAssigningUser(null);
        onRefresh();
        toast.success('✅ Subscription erfolgreich zugewiesen!');
      }
    } catch (error) {
      console.error('Error assigning subscription:', error);
      toast.error('Fehler beim Zuweisen der Subscription', {
        description: error.message || 'Unbekannter Fehler'
      });
    }
  };

  const handleAddCoins = async () => {
    if (!coinsAmount || coinsAmount === '0') {
      toast.error('Bitte einen Betrag eingeben!');
      return;
    }

    if (!coinsReason.trim()) {
      toast.error('Bitte einen Grund angeben!');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/credits/adjust`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: coinsUser.id,
          credit_type: 'permanent',
          delta: parseInt(coinsAmount),
          reason: coinsReason
        })
      });

      if (response.ok) {
        const result = await response.json();
        const newBalance = result.new_balance;
        const delta = parseInt(coinsAmount);
        const userName = coinsUser.name || coinsUser.email;
        
        // Show detailed success toast
        const action = delta > 0 ? 'gutgeschrieben' : 'abgezogen';
        toast.success(`✅ ${Math.abs(delta)} Coins ${action}!`, {
          description: `User: ${userName}\nNeue Balance: ${newBalance.coins} Coins (Täglich: ${newBalance.daily_credits} | Monatlich: ${newBalance.monthly_credits} | Permanent: ${newBalance.permanent_credits})`
        });
        
        setShowCoinsModal(false);
        setCoinsUser(null);
        setCoinsAmount('');
        setCoinsReason('');
        onRefresh();
      } else {
        const error = await response.json();
        console.error('❌ Server error:', error);
        toast.error('Fehler beim Gutschreiben', {
          description: error.detail || 'Unbekannter Fehler'
        });
      }
    } catch (error) {
      console.error('❌ Error adding coins:', error);
      toast.error('Fehler beim Gutschreiben der Coins!', {
        description: error.message || 'Netzwerk- oder Serverfehler'
      });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="user-management">
      <div className="management-header">
        <h2 className="management-title">User-Verwaltung</h2>
        <button onClick={() => setShowModal(true)} className="btn-create-user">
          + Neuer User
        </button>
      </div>

      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>E-Mail</th>
              <th>Rolle</th>
              <th>Registriert</th>
              <th>Onboarding</th>
              <th>Subscription</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td className="table-user-name">{u.name || '-'}</td>
                <td>{u.email}</td>
                <td>
                  <span className={`role-badge ${u.role}`}>
                    {u.role === 'admin' ? '🔑 Admin' : '👤 User'}
                  </span>
                  {u.is_practitioner && (
                    <span className="practitioner-badge" title="Practitioner">
                      🩺
                    </span>
                  )}
                </td>
                <td className="table-date">{formatDate(u.created_at)}</td>
                <td>
                  <span className={`onboarding-badge ${u.onboarding_completed ? 'completed' : 'pending'}`}>
                    {u.onboarding_completed ? '✅ Abgeschlossen' : '⏳ Ausstehend'}
                  </span>
                </td>
                <td>
                  {(() => {
                    // Same logic as backend: check subscription_tier if end_date is still valid
                    const subscriptionTier = u.subscription_tier;
                    const subscriptionPlan = u.subscription_plan || '';
                    const endDate = u.subscription_end_date;
                    
                    let effectiveTier = 'trial';
                    
                    // Check if subscription is still valid
                    if (endDate) {
                      const endDateTime = new Date(endDate);
                      if (endDateTime > new Date()) {
                        // Subscription still valid - use tier or derive from plan
                        if (subscriptionTier) {
                          effectiveTier = subscriptionTier;
                        } else if (subscriptionPlan.toLowerCase().includes('premium')) {
                          effectiveTier = 'premium';
                        } else if (subscriptionPlan.toLowerCase().includes('standard')) {
                          effectiveTier = 'standard';
                        }
                      }
                    } else if (subscriptionPlan) {
                      // Active subscription without end_date
                      if (subscriptionPlan.toLowerCase().includes('premium')) {
                        effectiveTier = 'premium';
                      } else if (subscriptionPlan.toLowerCase().includes('standard')) {
                        effectiveTier = 'standard';
                      }
                    }
                    
                    return (
                      <span className={`subscription-badge ${effectiveTier}`}>
                        {effectiveTier === 'premium' ? '💎 Premium' :
                         effectiveTier === 'standard' ? '⭐ Standard' : '🆓 Trial'}
                      </span>
                    );
                  })()}
                </td>
                <td className="table-actions-user">
                  <button
                    onClick={() => { setCoinsUser(u); setShowCoinsModal(true); }}
                    className="btn-coins"
                    title="Coins gutschreiben"
                    style={{background: 'linear-gradient(135deg, #c2a45a 0%, #d4b76a 100%)', color: '#000', border: 'none'}}
                  >
                    🪙
                  </button>
                  <button
                    onClick={() => { setAssigningUser(u); setShowAssignModal(true); }}
                    className="btn-assign-sub"
                    title="Subscription zuweisen"
                  >
                    💳
                  </button>
                  <button
                    onClick={() => handleEdit(u)}
                    className="btn-edit-user"
                    title="Bearbeiten"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleToggleLock(u.id, u.is_locked)}
                    className={`btn-lock-user ${u.is_locked ? 'locked' : ''}`}
                    title={u.is_locked ? 'Entsperren' : 'Sperren'}
                  >
                    {u.is_locked ? '🔓' : '🔒'}
                  </button>
                  <button
                    onClick={() => handleTogglePractitioner(u.id, u.is_practitioner)}
                    className={`btn-practitioner ${u.is_practitioner ? 'active' : ''}`}
                    title={u.is_practitioner ? 'Practitioner-Rolle entfernen' : 'Practitioner-Rolle zuweisen'}
                  >
                    🩺
                  </button>
                  <button
                    onClick={() => onLoginAsUser(u.id)}
                    className="btn-login-as"
                    disabled={u.role === 'admin' || u.is_locked}
                    title="Als User einloggen"
                  >
                    🔐
                  </button>
                  {deleteConfirm === u.id ? (
                    <div className="delete-confirm-inline">
                      <button
                        onClick={() => handleDelete(u.id)}
                        className="btn-delete-confirm-small"
                        title="Löschen bestätigen"
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="btn-delete-cancel-small"
                        title="Abbrechen"
                      >
                        ✗
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(u.id)}
                      className="btn-delete-user"
                      title="Löschen"
                    >
                      🗑️
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-container">
            <div className="modal-header">
              <h2 className="modal-title">{editingUser ? 'User bearbeiten' : 'Neuen User erstellen'}</h2>
              <button onClick={() => { setShowModal(false); setEditingUser(null); }} className="modal-close">
                ×
              </button>
            </div>

            <div className="modal-form">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Vorname"
                />
              </div>

              <div className="form-group">
                <label>E-Mail *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@beispiel.de"
                />
              </div>

              <div className="form-group">
                <label>Passwort {editingUser && '(leer lassen um nicht zu ändern)'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={editingUser ? 'Neues Passwort (optional)' : 'Mindestens 6 Zeichen'}
                />
              </div>

              <div className="form-group">
                <label>Rolle</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="modal-actions">
                <button onClick={() => { setShowModal(false); setEditingUser(null); }} className="btn-modal-cancel">
                  Abbrechen
                </button>
                <button onClick={handleCreate} className="btn-modal-save" disabled={loading}>
                  {loading ? 'Speichern...' : (editingUser ? 'Aktualisieren' : 'User erstellen')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Subscription Modal */}
      {showAssignModal && assigningUser && (
        <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowAssignModal(false)}>
          <div className="modal-container assign-modal">
            <div className="modal-header">
              <h2 className="modal-title">Subscription zuweisen</h2>
              <button onClick={() => setShowAssignModal(false)} className="modal-close">
                ×
              </button>
            </div>

            <div className="modal-form">
              <div className="assign-user-info">
                <strong>User:</strong> {assigningUser.name} ({assigningUser.email})
              </div>

              <div className="form-group">
                <label>Plan auswählen</label>
                <select
                  value={assignPlan}
                  onChange={(e) => setAssignPlan(e.target.value)}
                >
                  <option value="trial">🆓 Trial (Zurücksetzen)</option>
                  <option value="expired">❌ Expired (Abgelaufen)</option>
                  <option value="monthly">⭐ Standard Monatlich (20€)</option>
                  <option value="annual">⭐ Standard Jährlich (199€)</option>
                  <option value="premium_monthly">💎 Premium Monatlich (40€)</option>
                  <option value="premium_annual">💎 Premium Jährlich (399€)</option>
                </select>
              </div>

              <div className="assign-info">
                {assignPlan === 'trial' && (
                  <div style={{color: '#fbbf24'}}>
                    ⚠️ Setzt User zurück auf Trial-Status. Die aktuelle Subscription wird gelöscht.
                  </div>
                )}
                {assignPlan === 'expired' && (
                  <div style={{color: '#ef4444'}}>
                    ⚠️ Markiert die Subscription als abgelaufen. Nützlich zum Testen.
                  </div>
                )}
                {!['trial', 'expired'].includes(assignPlan) && (
                  <>
                    ℹ️ Die Subscription wird sofort aktiviert und läuft automatisch ab nach:
                    <ul>
                      <li>Monatlich: 30 Tage</li>
                      <li>Jährlich: 365 Tage</li>
                    </ul>
                  </>
                )}
              </div>

              <div className="modal-actions">
                <button onClick={() => setShowAssignModal(false)} className="btn-modal-cancel">
                  Abbrechen
                </button>
                <button onClick={handleAssignSubscription} className="btn-modal-save">
                  Subscription zuweisen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Coins Modal */}
      {showCoinsModal && (
        <div className="modal-overlay" onClick={() => setShowCoinsModal(false)}>
          <div className="modal-content-user" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-user">
              <h3>🪙 Coins gutschreiben</h3>
              <button
                onClick={() => setShowCoinsModal(false)}
                className="modal-close-user"
              >
                ×
              </button>
            </div>
            <div className="modal-body-user">
              <div className="user-info-box" style={{
                background: 'rgba(194, 164, 90, 0.1)',
                border: '1px solid rgba(194, 164, 90, 0.3)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px'
              }}>
                <p style={{margin: '4px 0', fontSize: '14px'}}>
                  <strong>User:</strong> {coinsUser?.name || coinsUser?.email}
                </p>
              </div>

              <div className="form-group-user">
                <label>Betrag (+ gutschreiben / - abziehen):</label>
                <input
                  type="number"
                  value={coinsAmount}
                  onChange={(e) => setCoinsAmount(e.target.value)}
                  placeholder="z.B. +100 oder -50"
                  className="form-input-user"
                />
                <small style={{color: '#888', display: 'block', marginTop: '5px'}}>
                  💡 Tipp: Negative Zahlen zum Abziehen verwenden
                </small>
              </div>

              <div className="form-group-user">
                <label>Grund (Pflichtfeld):</label>
                <textarea
                  value={coinsReason}
                  onChange={(e) => setCoinsReason(e.target.value)}
                  placeholder="z.B. Treue-Bonus, Support-Gutschrift, Fehlerkorrektur..."
                  rows="3"
                  className="form-input-user"
                />
              </div>

              <div className="modal-actions-user">
                <button onClick={() => setShowCoinsModal(false)} className="btn-modal-cancel">
                  Abbrechen
                </button>
                <button 
                  onClick={handleAddCoins} 
                  className="btn-modal-save"
                  style={{background: 'linear-gradient(135deg, #c2a45a 0%, #d4b76a 100%)', color: '#000'}}
                >
                  Coins gutschreiben
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUserManagement;
